// Globals
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getImportsList, productsSelector } from '../../feature';

// Components
import {
  StyledHeaderBlock,
  StyledPageTitle,
  StyledPageWrapper,
  StyledContentBlock,
} from './ImportsListContainerStyles';
import ImportsList from '../../components/ImportsList/ImportsList';

// Models
import { ImportStatusEnum, IProductsImportItem } from '@/models/productsImport.model';

// Hooks | Helpers
import { GetImportsListDTO } from '../../dtos';
import { usePagination } from '@/hooks';

// Refresh interval while an import is still running, so the imported-records
// count and the progress bar update in near real time.
const POLLING_INTERVAL = 4000;

const ImportsListContainer = () => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { isLoading, importsFilters, importsList } = useAppState(productsSelector);
  const { currentPage, onPageChange } = usePagination();

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const fetchImportsList = useCallback(() => {
    const dto = new GetImportsListDTO({ ...importsFilters, page: currentPage });
    dispatch(getImportsList(dto));
  }, [dispatch, importsFilters, currentPage]);

  useEffect(() => {
    fetchImportsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const hasProcessingImports = importsList?.data?.some((item) => item.status === ImportStatusEnum.PROCESSING);

  // Poll while at least one import is still processing; stop as soon as none are.
  useEffect(() => {
    if (hasProcessingImports) {
      if (!pollingRef.current) {
        setIsPolling(true);
        pollingRef.current = setInterval(fetchImportsList, POLLING_INTERVAL);
      }
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [hasProcessingImports, fetchImportsList, stopPolling]);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('imports_list_title')}</StyledPageTitle>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <ImportsList
          importsList={importsList?.data as IProductsImportItem[]}
          isLoading={isLoading && !isPolling}
          currentPage={currentPage}
          onPageChange={onPageChange}
          total={importsList?.meta.total ?? 0}
        />
      </StyledContentBlock>
    </StyledPageWrapper>
  );
};

export default ImportsListContainer;
